import {
  Button,
  Chip,
  ChipMeeting,
  ConcatText,
  Icon,
  Loading,
  Header,
  Text,
  CollectionsImage,
  ProfileInfo,
  ProfileBiography,
  ListSchedule,
  PostList,
  GlamourImage,
} from 'components';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  TCreateChannel,
  TUpsertFootPrint,
  TFemaleDetail,
  TRecommendFemale,
} from 'types';
import { PAGE_SIZE, TEXT_STRING, queryKeys, routes } from '../../constants';
import {
  createChannel,
  createClientChannel,
  getFemaleDetail,
  getFemalePosts,
  upsertFootPrint,
} from 'api';
import { useAuth, useFollow, useOrder } from 'hooks';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from 'store';
import { detectMobile, getFormattedAmount } from 'utils';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { Waypoint } from 'react-waypoint';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useUserStore } from 'store';
import LoadingPost from 'components/PostItem/LoadingPost';

const DETAIL_STR = TEXT_STRING.CAST_PAGE;
const COMMON_STR = TEXT_STRING.COMMON;

function DetailFemale() {
  // Hooks
  const { id } = useParams();
  const { checkNeedToLogin } = useAuth();
  const isMobile = detectMobile();
  const { handleFollow, loadingFollow } = useFollow();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserStore();
  const { gotoCall } = useOrder();
  const { user } = useUserStore();
  const { overallRankings } = useAppStore();

  // States
  const [following, setFollowing] = useState<boolean>(false);
  const [viewScroll, setViewScroll] = useState<boolean>(true);

  // Ref
  const footPrintCalled = useRef(false);

  // Query
  const { data, isLoading, isFetching } = useQuery<TFemaleDetail>({
    queryKey: [queryKeys.DETAIL_FEMALE, id],
    queryFn: () => getFemaleDetail(+(id || '')),
    enabled: !!id,
  });

  const {
    data: postData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingPost,
    refetch: refetchPostData,
  } = useInfiniteQuery({
    queryKey: [queryKeys.FEMALE_POST, id],
    queryFn: async ({ pageParam }) =>
      getFemalePosts({
        userId: +(id || 0),
        currentPage: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    enabled: !!id,
  });

  // Mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: TCreateChannel) => {
      return isAuthenticated ? createChannel(body) : createClientChannel(body);
    },
  });

  const { mutate: mutateUpsertFootPrint } = useMutation({
    mutationFn: (body: TUpsertFootPrint) => {
      return upsertFootPrint(body);
    },
    retry: 0,
  });

  // Memo, callbacks
  const basicInfo = useMemo(() => {
    return [
      {
        label: DETAIL_STR.AFFILIATION_AREA,
        value: data?.activityArea?.name,
      },
      {
        label: DETAIL_STR.AGE_NUMBER,
        value: `${data?.age}${COMMON_STR.AGE}`,
      },
      {
        label: DETAIL_STR.HEIGHT,
        value: `${data?.height}${DETAIL_STR.CM}`,
      },
      {
        label: DETAIL_STR.NUMBER_CUP,
        value: data?.cupSize,
      },
      {
        label: DETAIL_STR.NOMINATION_FEE,
        value: `${getFormattedAmount(data?.nominationFee ?? 0)}${
          COMMON_STR.YEN
        }`,
      },
    ];
  }, [data]);

  const getDiscountInfo = useCallback(
    (label: string, amount = 0, applied = 0) => ({
      label: label,
      amount: getFormattedAmount(amount),
      applied: getFormattedAmount(applied),
      disabled: !amount || !applied,
    }),
    [],
  );

  const discountInfo = useMemo(
    () => [
      getDiscountInfo(
        COMMON_STR.DISCOUNT_FOR_MULTIPLE,
        data?.groupDiscount?.amount,
        data?.groupDiscount?.applied,
      ),
      getDiscountInfo(
        COMMON_STR.DISCOUNT_FOR_ONE,
        data?.singleDiscount?.amount,
        data?.singleDiscount?.applied,
      ),
    ],
    [
      data?.groupDiscount?.amount,
      data?.groupDiscount?.applied,
      data?.singleDiscount?.amount,
      data?.singleDiscount?.applied,
      getDiscountInfo,
    ],
  );

  const toggleFollowFemale = useCallback(
    async (femaleId: number) => {
      if (checkNeedToLogin()) return;
      const succeeded = await handleFollow(femaleId, following);
      if (!succeeded) return;

      refetchPostData();
      setFollowing((prev) => !prev);
    },
    [
      checkNeedToLogin,
      following,
      handleFollow,
      refetchPostData,
    ],
  );

  // ロック中の投稿の「フォローして閲覧する」はフォローだけを行い、解除はしない
  const followFemaleToView = useCallback(
    async (femaleId: number) => {
      if (checkNeedToLogin()) return;
      if (following) {
        refetchPostData();
        return;
      }
      const succeeded = await handleFollow(femaleId, false);
      if (!succeeded) return;

      refetchPostData();
      setFollowing(true);
    },
    [checkNeedToLogin, following, handleFollow, refetchPostData],
  );

  const gotoConversation = useCallback(async () => {
    if (!data?.userId) return;
    try {
      let route = '';
      if (data?.channelId) {
        route = routes.CONVERSATION.replace(':id', data.channelId);
      } else {
        const channel = await mutateAsync({
          femaleId: data.userId,
        });
        route = routes.CONVERSATION.replace(':id', channel.id);
      }
      navigate(route);
    } catch (error) {
      toast.error((error as AxiosError).message);
    }
  }, [data, navigate, mutateAsync]);

  const isLoadingFollow = useMemo(
    () => loadingFollow?.includes(data?.userId ?? 0),
    [data?.userId, loadingFollow],
  );

  const handleGotoCall = useCallback(() => {
    if (!data) return;
    const nominatedFemale: TRecommendFemale = {
      id: data.userId,
      nickname: data.nickname,
      avatar: data.avatar,
      isPeriodTime: data.isPeriodTime,
      isMeeting: data.isMeeting,
      nominationFee: data.nominationFee,
    };
    gotoCall({ nominatedFemale });
  }, [data, gotoCall]);

  useEffect(() => {
    setFollowing(data?.isFollowed ?? false);
  }, [data?.isFollowed]);

  useEffect(() => {
    if (!user || !id || footPrintCalled.current) return;
    mutateUpsertFootPrint({ maleId: user.id, femaleId: id });
    footPrintCalled.current = true;
  }, [user, id, mutateUpsertFootPrint]);

  const postListView = useMemo(() => {
    return postData?.pages.map(({ data }) => data).flat() ?? [];
  }, [postData]);

  const overallRank = useMemo(() => {
    return overallRankings.find((ranking) => ranking.user.id === data?.userId)
      ?.rank;
  }, [overallRankings, data?.userId]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loading />
      </div>
    );
  }
  return (
    <div className="h-full">
      <Header
        title={
          <div className="flex items-center justify-center line-clamp-1">
            <ConcatText
              prefixText={data?.nickname}
              suffixText={data?.userId}
              className="font-bold line-clamp-1"
            />
            {data?.activityArea?.franchiseId !== 1 && (
              <Text bold fontSize={12}>
                （{DETAIL_STR.FRANCHISE}）
              </Text>
            )}
          </div>
        }
      />
      {data ? (
        <div className="h-full overflow-hidden pb-16">
          <InfiniteScroll
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<LoadingPost />}
            dataLength={postListView.length}
            scrollThreshold={0.5}
            height={'calc(100dvh - 68px - 46px)'}
          >
            <div className="overflow-hidden">
              {/* Avatar, collections */}
              <CollectionsImage
                avatar={data.avatar}
                collections={data.collections}
                rank={overallRank}
              />
              {/* Profile */}
              <Waypoint
                onEnter={() => {
                  setViewScroll(false);
                }}
                onLeave={() => {
                  setViewScroll(true);
                }}
                bottomOffset={'20%'}
              >
                <div className="px-4 pb-28">
                  <div className="pt-12px">
                    {/* active status */}
                    <div className="flex flex-wrap w-full justify-between gap-26px">
                      <div className="flex flex-col gap-10px">
                        <div className="flex items-center gap-4px">
                          <div
                            className={`w-12px h-12px rounded-full ${
                              !data.schedules.some(
                                (schedule) => !schedule.disabled,
                              ) &&
                              !data.isReadyToGo &&
                              !data.isToBeConfirmed
                                ? 'bg-neutral-400'
                                : 'bg-success-400'
                            } `}
                          ></div>
                          <Text bold fontSize={14}>
                            {DETAIL_STR.SCHEDULES}
                          </Text>
                        </div>
                        <ChipMeeting isMeeting={data.isMeeting} />
                        {overallRank && (
                          <Link
                            to={routes.RANKING}
                            className="flex items-center gap-1"
                          >
                            <Icon
                              icon="crown"
                              width={14}
                              height={14}
                              color="#fac515"
                            />
                            <Text bold fontSize={14}>
                              {DETAIL_STR.DAILY_RANK.replace(
                                '$rank',
                                overallRank.toString(),
                              )}
                            </Text>
                          </Link>
                        )}
                      </div>
                      {data.isPeriodTime && (
                        <div className="flex items-center self-start gap-[7px]">
                          <img
                            src="/images/menstruation.png"
                            alt=""
                            className="w-5 h-5"
                          />
                          <Text textColor="text-error-400" fontSize={14}>
                            {DETAIL_STR.PERIOD_MESSAGE}
                          </Text>
                        </div>
                      )}
                      <Button
                        onClick={() => toggleFollowFemale(data.userId)}
                        bgColor={
                          isLoadingFollow
                            ? 'bg-error-200'
                            : following
                              ? ''
                              : 'bg-error-400'
                        }
                        textColor={
                          following
                            ? isLoadingFollow
                              ? 'text-white'
                              : 'text-error-400'
                            : 'text-white'
                        }
                        border={following ? 'border' : ''}
                        className={`self-start ${
                          !isLoadingFollow && 'border-error-400'
                        }  ${data.isPeriodTime ? 'flex-1' : 'flex-[0.5]'} ${
                          isLoadingFollow && 'pointer-events-none'
                        } text-[14px]`}
                        bold
                      >
                        {following ? DETAIL_STR.FOLLOWING : DETAIL_STR.FOLLOW}
                      </Button>
                    </div>
                    {/* Profile schedules */}
                    <ListSchedule
                      schedules={data.schedules}
                      area={data.activityArea}
                      isToBeConfirmed={data.isToBeConfirmed}
                      enableReadyToGo={data.isReadyToGo}
                    />
                    {/* Profile detail */}
                    <div className="mt-[17px] flex flex-col gap-4 text-[14px]">
                      <ProfileInfo data={basicInfo} title={COMMON_STR.INFO} />
                      <Text
                        bold
                        className={`${
                          discountInfo.some((item) => !item.disabled)
                            ? ''
                            : 'hidden'
                        }`}
                      >
                        {COMMON_STR.DISCOUNT_COUPON}
                      </Text>
                      {discountInfo.map((item, index) => {
                        return (
                          !item.disabled && (
                            <Fragment key={index}>
                              <Text>{item.label}</Text>
                              <Text>
                                {COMMON_STR.GAME_FEE}
                                <b>
                                  {item.amount}
                                  {COMMON_STR.YEN}
                                </b>
                                {COMMON_STR.DISCOUNT_NOTE}
                                <b>
                                  {item.applied}
                                  {COMMON_STR.YEN}
                                </b>
                                {COMMON_STR.DISCOUNT}
                              </Text>
                            </Fragment>
                          )
                        );
                      })}
                      <ProfileBiography note={data.biography} />
                    </div>
                    <div className="flex flex-col gap-4 mt-22px text-[14px]">
                      <Text bold>{DETAIL_STR.RATING_BADGE}</Text>
                      <div className="overflow-x-auto">
                        <div className="flex gap-3 py-5">
                          {(data.rewardGifts ?? []).map((gift) => (
                            <div
                              key={gift.id}
                              className="flex flex-col items-center"
                            >
                              <div className="w-24 h-24 mb-1 relative">
                                <img
                                  src={gift.image}
                                  alt={gift.name}
                                  className="w-full h-full object-contain"
                                />
                                <div className="absolute -top-5 right-0">
                                  <div
                                    className="flex items-center justify-center rounded-full bg-gray-500 text-white text-xs font-medium h-10"
                                    style={gift.count <= 9 ? { aspectRatio: '1/1' } : { padding: '0 6px' }}
                                  >
                                    {gift.count <= 999 && (
                                      <Icon
                                        icon="close"
                                        color="var(--color-primary-50)"
                                        width={12}
                                      />
                                    )}
                                    <p className="mb-[2px]">
                                      {gift.count > 999
                                        ? '999+'
                                        : `${gift.count}`}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Female tags */}
                    <div className="flex flex-wrap gap-8px mt-22px">
                      {data.options?.map((option) => (
                        <Chip
                          key={option.id}
                          bgColor="bg-white"
                          textColor="text-neutral-600"
                          ringColor="ring-black"
                          className="text-[14px]"
                        >
                          {option?.label}
                        </Chip>
                      ))}
                    </div>
                    {(data.glamourImages || []).length > 0 && (
                      <GlamourImage images={data.glamourImages} />
                    )}
                  </div>
                  {/* List post */}
                  <>
                    <hr className="solid my-8" />
                    <Text textColor="text-primary" bold fontSize={14} center>
                      {COMMON_STR.RECENT_TWEETS}
                    </Text>
                    {isLoadingPost ? (
                      <LoadingPost />
                    ) : postListView?.length ? (
                      <PostList
                        data={postListView}
                        onFollowFemale={followFemaleToView}
                      />
                    ) : (
                      <Text center bold fontSize={16} className="mt-4">
                        {TEXT_STRING.COMMON.NO_DATA}
                      </Text>
                    )}
                  </>
                </div>
              </Waypoint>
            </div>
          </InfiniteScroll>
          <div
            className={`sticky bottom-0 ${
              isMobile ? 'w-full' : 'w-[50vh]'
            } p-12px mx-auto z-[2]`}
          >
            {viewScroll && (
              <div className="absolute -top-9 bg-black/50 left-1/2 -translate-x-1/2 py-2 px-10 backdrop-blur-sm rounded-full">
                <Icon icon="chevronDownDouble" />
              </div>
            )}
            <div className="flex gap-4">
              <Button
                className="w-full py-16px rounded-[8px] text-[14px]"
                onClick={gotoConversation}
                disabled={(!data.channelId && isFetching) || isPending}
                loading={isPending}
              >
                <Text
                  bold
                  center
                  textColor="text-white"
                  className="line-clamp-2 break-all"
                >
                  {DETAIL_STR.CHAT}
                </Text>
              </Button>
              <Button
                className="w-full py-16px rounded-[8px] text-[14px]"
                onClick={handleGotoCall}
              >
                <Text
                  bold
                  center
                  textColor="text-white"
                  className="line-clamp-2 break-all"
                >
                  {DETAIL_STR.CALL}
                </Text>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Text center bold fontSize={16} className="mt-4">
          {TEXT_STRING.COMMON.NO_DATA}
        </Text>
      )}
    </div>
  );
}

export default DetailFemale;
